import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import type { FunnelConfig } from "@nextday/funnel-engine";

import { isKindeCallbackUrl } from "@src/auth/AuthCallbackPage";
import { deobfuscate } from "@src/funnel/config/deobfuscate";
import { applyDocumentMetadata } from "@src/funnel/documentMetadata";
import { readOrCreateAnonymousId } from "@src/funnel/useAnonymousId";

/**
 * Runtime application context resolved by the funnel-api on boot.
 *
 * Multi-tenancy layer: a single funnel SPA bundle serves any application
 * (SpicyChat, PixelChat, GPTGirlfriend, SecretMate). Which application is
 * active depends on the deployed host and (for shared marketing domains)
 * the URL's campaign slug.
 *
 * The visible "brand" (logo, colors, copy) is a presentation concern. It's
 * derived from the resolved application's `themeTokens` so the data layer
 * stays consistent (always `application_id`) while the UI keeps the
 * marketing-friendly term.
 */
export type ResolvedApplication = {
  id: string;
  slug: string;
  name: string;
  paywallBaseUrl: string;
  themeTokens: Record<string, unknown>;
  /** NX-11852: Brand-default SEO metadata templates (`seo_json`). */
  seo: Record<string, unknown>;
  defaultLocale: string;
  kindeDomain: string | null;
  kindeOrgCode: string | null;
  kindeClientId: string | null;
  appOrigin: string | null;
  tapfiliateAccountId: string | null;
  ga4MeasurementId: string | null;
  analyticsEnabled: boolean;
  authProvider: string;
  clerkPublishableKey: string | null;
  externalSignupUrl: string | null;
  externalSigninUrl: string | null;
};

export type ResolvedInlet = {
  applicationId: string;
  inletId: string;
  defaultFunnelId: string;
  defaultCharacterId: string | null;
};

export type ResolvedFunnel = {
  application_id: string;
  config: FunnelConfig;
  /** Per-funnel display overrides (NX-9597). */
  display_config: Record<string, unknown>;
  /** NX-11852: Per-funnel SEO metadata templates (`seo_json`). */
  seo: Record<string, unknown>;
  /** NX-11852: Funnel name, available as the `{funnel_name}` metadata token. */
  name: string;
  /** Running experiment id when the variant was assigned via bucketing (NX-9773). */
  experiment_id?: string;
  /** NX-9949: Experiment arm id assigned via bucketing. */
  arm_id?: string;
  /** NX-9847: Published funnel version number. Omitted when serving from draft tables. */
  version?: number;
  /** NX-10213: True when resolved in preview/draft mode. */
  is_preview?: boolean;
};

export type ApplicationContextState =
  | { status: "loading" }
  | {
      status: "ready";
      application: ResolvedApplication;
      inlet: ResolvedInlet | null;
      funnel: ResolvedFunnel | null;
      /**
       * NX-12391: The API served a last-known-good snapshot because the
       * session store was unavailable. The funnel renders normally; the flag
       * tells the session layer not to bother writing a session that cannot
       * be written.
       */
      degraded?: true;
    }
  | { status: "error"; message: string };

const RESERVED_SEGMENTS = new Set(["api", "lp", "storybook"]);

/**
 * Extract the application path prefix from the first URL segment.
 * E.g. `/sm/lp/fb-may` → `"sm"`, `/sm/` → `"sm"`, `/` → `null`.
 *
 * Reserved segments (`api`, `lp`, `storybook`) at the root level are not
 * treated as prefixes — they keep their existing semantics.
 */
export const extractAppPrefixFromPath = (pathname: string): string | null => {
  const match = /^\/([^/?#]+)/.exec(pathname);
  if (!match) return null;
  const segment = match[1]!;
  if (RESERVED_SEGMENTS.has(segment)) return null;
  return segment;
};

export const extractInletSlugFromPath = (pathname: string): string | null => {
  // With path-prefix: /:prefix/lp/<inletSlug>
  // Only match when the first segment is a real app prefix (not reserved).
  const withPrefix = /^\/([^/?#]+)\/lp\/([^/?#]+)/.exec(pathname);
  if (withPrefix && !RESERVED_SEGMENTS.has(withPrefix[1]!)) {
    return withPrefix[2] ?? null;
  }
  // Without prefix: /lp/<inletSlug>
  const withoutPrefix = /^\/lp\/([^/?#]+)/.exec(pathname);
  if (withoutPrefix) return withoutPrefix[1] ?? null;
  // Short form: /:prefix/<inletSlug> (no /lp/ segment).
  // Any second segment after a non-reserved prefix is treated as an
  // inlet slug. The API validates it against the inlets table —
  // unrecognised slugs are ignored and the default funnel is served.
  const shortForm = /^\/([^/?#]+)\/([^/?#]+)/.exec(pathname);
  if (shortForm && !RESERVED_SEGMENTS.has(shortForm[1]!)) {
    return shortForm[2] ?? null;
  }
  return null;
};

const buildInitUrl = (prefix: string | null, inletSlug: string | null): string => {
  const search = new URLSearchParams();
  if (prefix) search.set("prefix", prefix);
  if (inletSlug) search.set("inletSlug", inletSlug);

  // Forward funnel_id + override_id + preview from the landing URL (QA/testing overrides).
  const landingParams = new URLSearchParams(window.location.search);
  const funnelId = landingParams.get("funnel_id");
  const overrideId = landingParams.get("override_id");
  const preview = landingParams.get("preview");
  if (funnelId) search.set("funnel_id", funnelId);
  if (overrideId) search.set("override_id", overrideId);
  // NX-10213: Forward preview flag so /api/init can load draft funnels.
  if (preview === "1") search.set("preview", "1");

  const qs = search.toString();
  return qs ? `/api/init?${qs}` : "/api/init";
};

export const stampFunnelIdInUrl = (funnelId: string | null | undefined): void => {
  if (funnelId === null || funnelId === undefined || funnelId === "") return;
  if (typeof window === "undefined") return;
  if (typeof window.history?.replaceState !== "function") return;

  const url = new URL(window.location.href);
  if (url.searchParams.get("funnel_id") === funnelId) {
    return;
  }

  url.searchParams.set("funnel_id", funnelId);
  window.history.replaceState(window.history.state, "", url.toString());
};

/**
 * Local-dev override: when `VITE_DEV_APPLICATION_SLUG` is set (e.g.
 * `secretmate`), the slug is used as the `prefix` for the `/api/init`
 * call instead of extracting it from the URL path. This lets developers
 * test a specific application without matching the URL structure.
 *
 * Never shipped to prod builds (guarded on `import.meta.env.DEV`).
 */
const readDevOverrideSlug = (): string | null => {
  // Cast narrowly; Vite injects env at build time.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  if (!env.DEV) return null;
  return env.VITE_DEV_APPLICATION_SLUG ?? null;
};

const INIT_MAX_RETRIES = 3;
const INIT_BASE_DELAY_MS = 1_000;

/**
 * Returns true for HTTP status codes that are worth retrying — server errors
 * and 404 (can be transient during Cloudflare Pages/Worker deployments).
 */
const isRetryableStatus = (status: number): boolean =>
  status >= 500 || status === 404 || status === 429;

const abortableDelay = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason as Error);
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal.reason as Error);
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Fetch with exponential-backoff retries for transient failures.
 * Retries on network errors, 5xx, 429, and 404 (transient during deploys).
 * Respects the AbortSignal between retries so unmounts cancel cleanly.
 */
const fetchWithRetry = async (
  url: string,
  options: RequestInit & { signal: AbortSignal },
): Promise<Response> => {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(url, options);
      if (attempt < INIT_MAX_RETRIES && isRetryableStatus(response.status)) {
        await abortableDelay(INIT_BASE_DELAY_MS * 2 ** attempt, options.signal);
        continue;
      }
      return response;
    } catch (error) {
      if ((error as Error).name === "AbortError") throw error;
      if (attempt < INIT_MAX_RETRIES) {
        await abortableDelay(INIT_BASE_DELAY_MS * 2 ** attempt, options.signal);
        continue;
      }
      throw error;
    }
  }
};

// Hook that owns the actual fetch + applyThemeTokens side effect. Internal —
// only the provider should call it, exactly once per page load. Consumers
// read the result via `useApplicationContext()` (the context hook below).
const useResolveApplicationContext = (): ApplicationContextState => {
  const [state, setState] = useState<ApplicationContextState>({ status: "loading" });

  useEffect(() => {
    // Skip the init fetch on Kinde auth callback URLs — the callback page
    // doesn't need application context (auth config is in sessionStorage)
    // and the callback URL has no path prefix, so the fetch would 404.
    // Kinde SDK will process the callback and redirect before the user
    // sees anything, but set a defensive status in case the redirect stalls.
    if (isKindeCallbackUrl(window.location.search)) {
      setState({ status: "loading" });
      return;
    }

    const controller = new AbortController();

    const load = async (): Promise<void> => {
      const devSlug = readDevOverrideSlug();
      const prefix = devSlug ?? extractAppPrefixFromPath(window.location.pathname);
      const inletSlug = devSlug ? null : extractInletSlugFromPath(window.location.pathname);
      try {
        const anonymousId = readOrCreateAnonymousId();

        const initResponse = await fetchWithRetry(buildInitUrl(prefix, inletSlug), {
          headers: { accept: "text/plain", "x-anonymous-id": anonymousId },
          signal: controller.signal,
          credentials: "same-origin",
        });

        if (!initResponse.ok) {
          setState({
            status: "error",
            message: `init_failed:${initResponse.status}`,
          });
          return;
        }

        const contentType = initResponse.headers.get("content-type") ?? "";
        if (!contentType.startsWith("text/plain")) {
          setState({ status: "error", message: "init_unexpected_content_type" });
          return;
        }

        const encoded = await initResponse.text();
        const json = deobfuscate(encoded);
        const payload: unknown = JSON.parse(json);

        if (typeof payload !== "object" || payload === null || !("application" in payload)) {
          setState({ status: "error", message: "init_malformed_payload" });
          return;
        }

        const {
          application,
          inlet,
          funnel: rawFunnel,
          degraded,
        } = payload as {
          application: ResolvedApplication;
          inlet: ResolvedInlet | null;
          funnel: unknown;
          degraded?: unknown;
        };

        // Validate funnel shape when present — prevents malformed config from
        // reaching the engine and causing an unhandled runtime error.
        let funnel: ResolvedFunnel | null = null;
        if (rawFunnel !== null && typeof rawFunnel === "object") {
          const f = rawFunnel as Record<string, unknown>;
          const cfg = f.config as Record<string, unknown> | undefined;
          if (
            typeof f.application_id === "string" &&
            cfg &&
            typeof cfg.id === "string" &&
            (typeof cfg.overrideId === "string" || cfg.overrideId === null) &&
            Array.isArray(cfg.steps)
          ) {
            // NX-9597: Ensure display_config is always a record.
            const dc = f.display_config;
            const displayConfig: Record<string, unknown> =
              typeof dc === "object" && dc !== null && !Array.isArray(dc)
                ? (dc as Record<string, unknown>)
                : {};
            const seo = f.seo;
            funnel = {
              application_id: f.application_id,
              config: f.config as FunnelConfig,
              display_config: displayConfig,
              seo:
                typeof seo === "object" && seo !== null && !Array.isArray(seo)
                  ? (seo as Record<string, unknown>)
                  : {},
              name: typeof f.name === "string" ? f.name : "",
              ...(typeof f.experiment_id === "string" ? { experiment_id: f.experiment_id } : {}),
              ...(typeof f.arm_id === "string" ? { arm_id: f.arm_id } : {}),
              ...(typeof f.version === "number" ? { version: f.version } : {}),
              ...(f.is_preview === true ? { is_preview: true } : {}),
            };
          }
        }

        applyThemeTokens(application.themeTokens);
        applyDocumentMetadata(application, funnel, inletSlug);
        stampFunnelIdInUrl(funnel?.config.id);
        setState({
          status: "ready",
          application,
          inlet,
          funnel,
          ...(degraded === true ? { degraded: true as const } : {}),
        });
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        setState({ status: "error", message: "network_error" });
      }
    };

    void load();

    return () => controller.abort();
  }, []);

  return state;
};

const toCssVar = (key: string): string =>
  key.startsWith("--") ? key : `--${key.replace(/_/g, "-")}`;

/**
 * A brand background is "dark" when the lightness component of its
 * `H S% L%` HSL triplet is below 50%. Unparseable or missing values fall back
 * to light, matching the `:root` defaults in funnel-ui's tokens.css.
 */
const isDarkBackground = (brandBg: string | undefined): boolean => {
  const lightness = parseFloat(brandBg?.trim().split(/\s+/)[2] ?? "");
  return !Number.isNaN(lightness) && lightness < 50;
};

/**
 * HeroUI reads its own palette (`--field-background`, `--default`, …) from the
 * `.dark` / `[data-theme]` blocks on the root element. index.html ships `dark`
 * to avoid a light flash, so light-brand funnels must switch it back or their
 * inputs, chips and fields render with dark surfaces.
 */
const applyThemeMode = (brandBg: string | undefined): void => {
  const root = document.documentElement;
  const dark = isDarkBackground(brandBg);
  root.classList.toggle("dark", dark);
  root.setAttribute("data-theme", dark ? "dark" : "default");
  root.style.colorScheme = dark ? "dark" : "light";
};

export const applyThemeTokens = (tokens: Record<string, unknown>): void => {
  const root = document.documentElement;
  Object.entries(tokens).forEach(([token, value]) => {
    if (typeof value === "string") {
      root.style.setProperty(toCssVar(token), value);
    }
  });
  const brandBg = tokens["brand_bg"] ?? tokens["--brand-bg"];
  applyThemeMode(typeof brandBg === "string" ? brandBg : undefined);
};

// React context that exposes the resolved state to anyone in the tree. The
// provider owns the single fetch; every consumer reads the same state. The
// undefined sentinel lets `useApplicationContext` throw a clear error if a
// component is rendered outside the provider, instead of silently returning
// "loading" forever.
const ApplicationContext = createContext<ApplicationContextState | undefined>(undefined);

export const ApplicationContextProvider = ({ children }: { children: ReactNode }) => {
  const state = useResolveApplicationContext();
  return createElement(ApplicationContext.Provider, { value: state }, children);
};

export const useApplicationContext = (): ApplicationContextState => {
  const state = useContext(ApplicationContext);
  if (state === undefined) {
    throw new Error("useApplicationContext must be used inside <ApplicationContextProvider>");
  }
  return state;
};
